import { Component, Input, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FetchProjectMembersListRequest } from '../../../../../core/projects.actions';
import { projectMembersListInfo } from '../../../../../core/projects.selectors';
import { AuthService, AppService } from '../../../../../../../shared/services';
import { AppHttpService } from '../../../../../../../shared/services/app-http.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { ProjectsService } from '../../../../../core/projects.service';
import { WARRANTY_STATES } from './warranty-state.util';

/**
 * Right-panel for a SINGLE warranty milestone task, following the
 * claim-detail panel idiom: Details/History tabs, an actions dropdown
 * ("Take action on this task") with comments + attachments, and a
 * full-width Submit button pinned at the bottom.
 * Create mode (data.milestoneKey, no task) shows the create form instead.
 */
@Component({
  selector: 'app-warranty-tasks',
  templateUrl: './warranty-tasks.component.html',
  styleUrls: ['./warranty-tasks.component.scss'],
  standalone: false,
})
export class WarrantyTasksComponent implements OnInit, OnDestroy {
  @Input() data: any;

  projectId: string;
  warranty: any;
  task: any = null;
  milestoneKey: string = null;
  role = '';
  currentUserUuid: string = null;
  selectedTabIndex = 0;

  taskForm: FormGroup;

  actionForm: FormGroup;
  actionOptions: { value: string; label: string }[] = [];
  actionMediaFiles: any[] = [];

  memberOptions: any[] = [];

  statusMap: Record<string, { label: string; chipClass: string }> = {
    open: { label: 'Open', chipClass: 'chip-open' },
    closed: { label: 'Closed', chipClass: 'chip-closed' },
  };

  milestoneLabels: Record<string, string> = WARRANTY_STATES
    .reduce((acc, s) => ({ ...acc, [s.key]: s.label }), {});

  eventLabels: Record<string, string> = {
    created: 'Created',
    assigned: 'Assigned',
    reassigned: 'Reassigned',
    unassigned: 'Unassigned',
    updated: 'Updated',
    closed: 'Closed',
    reopened: 'Reopened',
  };

  historyEntries: any[] = [];

  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store,
    private authService: AuthService,
    public appService: AppService,
    private appHttpService: AppHttpService,
    private commonService: CommonService,
    private projectsService: ProjectsService,
    private cdRef: ChangeDetectorRef,
  ) {
    this.taskForm = new FormGroup({
      title: new FormControl('', Validators.required),
      description: new FormControl(''),
      due_date: new FormControl(''),
      assigned_to: new FormControl(''),
    });
    this.actionForm = new FormGroup({
      action: new FormControl(''),
      notes: new FormControl(''),
      assigned_to: new FormControl(''),
    });
  }

  ngOnInit() {
    this.projectId = this.data?.projectId;
    this.warranty = this.data?.warranty;
    this.task = this.data?.task ? { ...this.data.task } : null;
    this.milestoneKey = this.data?.milestoneKey || this.task?.milestone_key || null;

    const account = this.authService.getCurrentUser();
    this.currentUserUuid = (account as any)?.uuid || account?.id || null;
    this.role = account?.roles?.find(r => r.project_id === this.projectId)?.role
      || account?.role
      || '';

    this.store.dispatch(FetchProjectMembersListRequest({ payload: { projectId: this.projectId } }));
    this.store.select(projectMembersListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data?.list) {
          this.memberOptions = data.list.map(m => ({
            value: m.uuid || m.id,
            label: `${m.first_name} ${m.last_name}` + (m.email ? ` (${m.email})` : ''),
          }));
          this.cdRef.detectChanges();
        }
      });

    // Files uploaded from the attachments tile (deep clone avoids frozen NgRx objects)
    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const items = JSON.parse(JSON.stringify(Array.isArray(data) ? data : [data]));
        const newFiles = items
          .filter(i => i?.fileType === 'picture' || i?.fileType === 'document')
          .map(f => ({ ...f, tags: ['actions-attachments'] }));
        if (newFiles.length) {
          this.actionMediaFiles = [...this.actionMediaFiles, ...newFiles];
          setTimeout(() => this.projectsService.mediaData.next(this.actionMediaFiles));
        }
      });

    if (this.task) {
      this.buildHistory();
      this.buildActionOptions();
    }
  }

  get isCreateMode(): boolean {
    return !this.task;
  }

  get warrantyId(): string {
    return this.warranty?.uuid || this.warranty?.id;
  }

  /** Serialized records expose uuid as `id`. */
  get taskId(): string {
    return this.task?.uuid || this.task?.id;
  }

  get milestoneLabel(): string {
    return this.milestoneLabels[this.milestoneKey] || this.milestoneKey || '';
  }

  get selectedAction(): string {
    return this.actionForm.get('action')?.value || '';
  }

  get canSubmitAction(): boolean {
    const action = this.selectedAction;
    if (!action) return false;
    if (action === 'reassign') return !!this.actionForm.get('assigned_to')?.value;
    return true;
  }

  canManage(): boolean {
    // Mirrors the API: anyone with update on Warranty (PM/org-admin/inspectors)
    return ['project-manager', 'org-admin', 'inspector', 'lead-inspector', 'contractor-inspector']
      .includes(this.role);
  }

  isAssignee(): boolean {
    const assignee = this.task?.assignedTo;
    if (!assignee || !this.currentUserUuid) return false;
    return assignee.uuid === this.currentUserUuid || assignee.id === this.currentUserUuid;
  }

  assigneeName(): string {
    const a = this.task?.assignedTo;
    if (!a) return 'Unassigned';
    return `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email || 'Unassigned';
  }

  /**
   * Build dropdown options based on task status + user role.
   */
  buildActionOptions() {
    this.actionOptions = [];
    if (!this.task) return;

    if (this.task.status === 'open') {
      if (this.canManage() || this.isAssignee()) {
        this.actionOptions.push({ value: 'close', label: 'Close task' });
      }
      if (this.canManage()) {
        this.actionOptions.push({
          value: 'reassign',
          label: this.task.assignedTo ? 'Reassign' : 'Assign',
        });
        if (this.task.assignedTo) {
          this.actionOptions.push({ value: 'unassign', label: 'Unassign' });
        }
      }
    } else if (this.task.status === 'closed' && this.canManage()) {
      this.actionOptions.push({ value: 'reopen', label: 'Reopen' });
    }

    this.actionForm.get('action')?.setValue('');
    this.actionForm.get('notes')?.setValue('');
    this.actionForm.get('assigned_to')?.setValue('');
    this.actionMediaFiles = [];
  }

  private closePanel() {
    this.projectsService.closeRightPanel.next();
  }

  /** Re-fetch this task (list endpoint + filter) after assign/reopen. */
  private reloadTask() {
    this.appHttpService.getService(
      `/v1/warranty/project/${this.projectId}/warranties/${this.warrantyId}/tasks`,
    ).pipe(takeUntil(this.onDestroy)).subscribe((resp: any) => {
      const rows = (resp?.data || []).map(t => ({ ...t, ...(t.attributes || {}) }));
      const fresh = rows.find(t => (t.uuid || t.id) === this.taskId);
      if (fresh) {
        this.task = fresh;
        this.buildHistory();
        this.buildActionOptions();
        this.cdRef.detectChanges();
      }
    });
  }

  // ----- Create -----

  createTask() {
    if (!this.taskForm.valid || !this.milestoneKey) return;
    const val = this.taskForm.value;
    const payload: any = {
      milestone_key: this.milestoneKey,
      title: val.title,
    };
    if (val.description) payload.description = val.description;
    if (val.due_date) payload.due_date = val.due_date;
    if (val.assigned_to) payload.assigned_to = val.assigned_to;

    this.appHttpService.postService(
      `/v1/warranty/project/${this.projectId}/warranties/${this.warrantyId}/tasks`,
      payload,
    ).subscribe({
      next: () => {
        this.commonService.notification('Task created.', 'success');
        this.closePanel();
      },
      error: () => this.commonService.notification('Failed to create task.', 'error'),
    });
  }

  // ----- Actions -----

  executeAction() {
    const action = this.selectedAction;
    if (!action || !this.task) return;
    const base = `/v1/warranty/project/${this.projectId}/warranties/${this.warrantyId}/tasks/${this.taskId}`;

    switch (action) {
      case 'close':
        this.appHttpService.postService(`${base}/close`, {
          notes: this.actionForm.get('notes')?.value || '',
          attachments: this.actionMediaFiles.map(f => f.id),
        }).subscribe({
          next: () => {
            this.commonService.notification('Task closed.', 'success');
            this.closePanel();
          },
          error: () => this.commonService.notification('Failed to close task.', 'error'),
        });
        break;
      case 'reassign':
        this.appHttpService.patchService(base, {
          assigned_to: this.actionForm.get('assigned_to')?.value,
        }).subscribe({
          next: () => {
            this.commonService.notification('Task assigned.', 'success');
            this.reloadTask();
          },
          error: () => this.commonService.notification('Failed to assign task.', 'error'),
        });
        break;
      case 'unassign':
        this.appHttpService.patchService(base, { assigned_to: null }).subscribe({
          next: () => {
            this.commonService.notification('Task unassigned.', 'success');
            this.reloadTask();
          },
          error: () => this.commonService.notification('Failed to unassign task.', 'error'),
        });
        break;
      case 'reopen':
        this.appHttpService.postService(`${base}/reopen`, {}).subscribe({
          next: () => {
            this.commonService.notification('Task reopened.', 'success');
            this.reloadTask();
          },
          error: () => this.commonService.notification('Failed to reopen task.', 'error'),
        });
        break;
    }
  }

  deleteActionPicture(pic: any) {
    this.actionMediaFiles = this.actionMediaFiles.filter(p => p.id !== pic.id);
    setTimeout(() => this.projectsService.mediaData.next(this.actionMediaFiles));
  }

  // ----- History -----

  private buildHistory() {
    const entries = [];
    for (const event of (this.task?.events || [])) {
      const d = event?.data || {};
      entries.push({
        uuid: event.uuid || event.id,
        date: event.created_at,
        action: this.historyAction(event),
        actor_name: d.actor_name || 'System',
        actor_initials: this.initials(d.actor_name),
        notes: event.type === 'closed' ? (d.notes || null) : null,
        attachments: event.type === 'closed' ? (d.attachments || []) : [],
      });
    }
    entries.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    this.historyEntries = entries;
  }

  private historyAction(event: any): string {
    const label = this.eventLabels[event?.type] || event?.type || '';
    const d = event?.data || {};
    if (event?.type === 'assigned' || event?.type === 'reassigned') {
      return `${label} to ${d.assignee_name || 'user'}`;
    }
    return label;
  }

  private initials(name: string): string {
    if (!name) return '•';
    return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
